import { Client } from "@stomp/stompjs";
import SockJS from "sockjs-client";
import { WS_URL } from "../config";
import { store } from "../../Store";
import { debugLog } from "../../Utils/utils";
import { CSPHelpers } from "../../Utils/security";

// Stop retrying after this many consecutive failed reconnects triggered by
// server ERROR frames — prevents hammering a backend that rejects every session
const MAX_ERROR_RECONNECT_ATTEMPTS = 8;

class ConnectionManager {
  constructor() {
    this.client = null;
    this.connected = false;
    this._connecting = false;
    this.currentChatId = null;
    this._messageHandlers = [];
    this._reconnectListeners = [];
    this._lastConnectedAt = null;
    this._connectedToken = null;
    this._reconnectAttempt = 0;
    this._healthCheckInterval = null;
    // onConnected callbacks handed to connect() while another connect was
    // still in flight — they fire when that connection completes
    this._pendingConnectCallbacks = [];

    // Will be set by StompChatService after wiring
    this._subscriptionManager = null;

    this._setupVisibilityHandler();
    this._setupHealthCheck();
  }

  // --- Exponential backoff ---
  _getReconnectDelay() {
    const delay = Math.min(1000 * Math.pow(2, this._reconnectAttempt), 30000);
    this._reconnectAttempt++;
    return delay;
  }

  // --- Visibility change detection ---
  _setupVisibilityHandler() {
    if (typeof document === "undefined") return;

    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState !== "visible") return;
      if (!this.client) return;

      if (!this.connected || !this.client.connected) {
        debugLog("[StompChat] Tab resumed, WebSocket dead — forcing reconnect");
        this._forceReconnect();
      } else {
        this._notifyReconnectListeners();
      }
    });
  }

  // --- Periodic health check ---
  _setupHealthCheck() {
    if (typeof setInterval === "undefined") return;
    this._healthCheckInterval = setInterval(() => {
      if (!this.client || !this.connected) return;
      if (!this.client.connected) {
        debugLog("[StompChat] Health check: STOMP client disconnected, reconnecting");
        this._forceReconnect();
      }
    }, 30000);
  }

  // Force a clean reconnect cycle
  _forceReconnect() {
    if (!this.client) return;
    this.connected = false;
    this._connecting = false;
    try {
      this.client.deactivate().then(() => {
        this.client.activate();
      });
    } catch (e) {
      this.client.activate();
    }
  }

  // Register a listener that fires after reconnect
  onReconnect(handler) {
    this._reconnectListeners.push(handler);
    return () => {
      this._reconnectListeners = this._reconnectListeners.filter((h) => h !== handler);
    };
  }

  _notifyReconnectListeners() {
    this._reconnectListeners.forEach((handler) => {
      try { handler(); } catch (e) { debugLog("Reconnect listener error:", e); }
    });
  }

  // Register a message handler (returns unsubscribe function)
  onMessage(handler) {
    this._messageHandlers.push(handler);
    return () => {
      this._messageHandlers = this._messageHandlers.filter((h) => h !== handler);
    };
  }

  // Notify all registered message handlers
  _notifyMessageHandlers(payload) {
    this._messageHandlers.forEach((handler) => {
      try {
        handler(payload);
      } catch (e) {
        debugLog("Error in message handler:", e);
      }
    });
  }

  // Fire (once) every callback that was waiting on the in-flight connection
  _flushPendingConnectCallbacks(frame) {
    if (!this._pendingConnectCallbacks.length) return;
    const pending = this._pendingConnectCallbacks;
    this._pendingConnectCallbacks = [];
    pending.forEach((handler) => {
      try { handler(frame); } catch (e) { debugLog("Pending connect callback error:", e); }
    });
  }

  // Drop callbacks that can never fire because the attempt was abandoned
  // (retry cap reached, or an explicit disconnect). They are NOT dropped on a
  // transient socket close: stompjs keeps retrying and onConnect will flush
  // them, which is the whole point of holding them
  _discardPendingConnectCallbacks(reason) {
    if (!this._pendingConnectCallbacks.length) return;
    debugLog(
      `[StompChat] Dropping ${this._pendingConnectCallbacks.length} pending connect callback(s): ${reason}`
    );
    this._pendingConnectCallbacks = [];
  }

  // Connect to WebSocket server
  connect(jwtToken, onConnected, onMessage, onError) {
    if (this.connected && this.client) {
      // Reconnect whenever the identity on the wire is not the one being
      // offered.
      //
      // This used to ask whether the live connection had no token at all and
      // call that "a guest". But a guest has a token too — the guest session
      // hands one out, and AuthSlice connects with it exactly as signing in
      // does. So signing in over a live guest connection looked like the same
      // identity connecting twice, and the socket was left where it was: the
      // visitor signed in and went on being listened to as a guest, until the
      // guest connection reasserted itself and put them back.
      //
      // Comparing the tokens needs no knowledge of which kind either one is,
      // and covers signing out, and signing in as someone else, for free.
      const differentIdentity = !!jwtToken && jwtToken !== this._connectedToken;
      if (differentIdentity) {
        this.disconnect();
      } else {
        if (onMessage) { this.onMessage(onMessage); }
        if (onConnected) { onConnected(); }
        return;
      }
    }

    if (this._connecting) {
      if (onMessage) { this.onMessage(onMessage); }
      // A connect is already in flight. Hold the callback instead of dropping
      // it — otherwise this caller never learns the socket came up and waits
      // forever (a question asked before the socket is ready is never sent)
      if (onConnected) { this._pendingConnectCallbacks.push(onConnected); }
      return;
    }

    this._connecting = true;

    if (onMessage) { this.onMessage(onMessage); }

    const socketUrl = WS_URL;

    const connectHeaders = {
      session_id: CSPHelpers.generateNonce(),
      Chat: true,
      connectionType: "customerService",
    };
    if (jwtToken) {
      connectHeaders.Authorization = `Bearer ${jwtToken}`;
    }

    this.client = new Client({
      webSocketFactory: () => new SockJS(socketUrl),
      connectHeaders,
      reconnectDelay: this._getReconnectDelay(),
      heartbeatIncoming: 10000,
      heartbeatOutgoing: 10000,
      onConnect: (frame) => {
        const wasConnectedBefore = this._lastConnectedAt !== null;
        this.connected = true;
        this._connecting = false;
        this._lastConnectedAt = Date.now();
        // Reset backoff on successful connection
        this._reconnectAttempt = 0;
        // Reset reconnect delay to base for future disconnects
        if (this.client) {
          this.client.reconnectDelay = this._getReconnectDelay();
          // Reset again since _getReconnectDelay increments
          this._reconnectAttempt = 0;
        }

        const activeAuth = this.client.connectHeaders?.Authorization;
        this._connectedToken = activeAuth
          ? activeAuth.replace("Bearer ", "")
          : null;
        if (onConnected) { onConnected(frame); }
        this._flushPendingConnectCallbacks(frame);

        if (wasConnectedBefore) {
          debugLog("[StompChat] Reconnected — notifying listeners to catch up on missed messages");
          setTimeout(() => this._notifyReconnectListeners(), 500);
        }

        const activeToken = jwtToken || store.getState().auth?.token;
        if (activeToken) {
          const currentUserId = store.getState().auth.user?.id;
          this.client.subscribe(
            `/socket-user/${currentUserId}/topic/chat`,
            (msg) => {
              const payload = JSON.parse(msg.body);
              if (
                payload.content ===
                "Sorry, I encountered an error processing your request."
              ) {
                return;
              }
              this.currentChatId = payload.chatId;
              this._notifyMessageHandlers(payload);
            }
          );

          this.client.subscribe(
            `/socket-user/${currentUserId}/topic/private`,
            (msg) => {
              const payload = JSON.parse(msg.body);
              if (
                payload.content ===
                "Sorry, I encountered an error processing your request."
              ) {
                return;
              }
              this._notifyMessageHandlers(payload);
            }
          );
        }

        // Delegate subscription restoration to SubscriptionManager
        if (this._subscriptionManager) {
          this._subscriptionManager._restoreSubscriptions(this);
        }
      },
      onStompError: (frame) => {
        debugLog("STOMP Error:", frame);

        if (frame.command === "ERROR") {
          const errorMessage = frame.headers?.message || "";

          if (errorMessage.includes("clientInboundChannel")) {
            this.connected = false;
            this._connecting = false;

            const failedAuth = this.client.connectHeaders?.Authorization;
            const failedToken = failedAuth
              ? failedAuth.replace("Bearer ", "")
              : null;
            const freshToken = store.getState().auth?.token;

            // Only retry immediately if the store actually has a different
            // token — reconnecting with the same rejected token just loops
            if (freshToken && freshToken !== failedToken) {
              debugLog(
                "clientInboundChannel error (stale session), reconnecting with fresh token:",
                errorMessage
              );
              this.client.reconnectDelay = 0;
              this.client.deactivate().then(() => {
                this.client.connectHeaders = {
                  ...this.client.connectHeaders,
                  Authorization: `Bearer ${freshToken}`,
                };
                this._reconnectAttempt = 0;
                this.client.reconnectDelay = this._getReconnectDelay();
                this._reconnectAttempt = 0;
                this.client.activate();
              });
              return;
            }

            if (this._reconnectAttempt >= MAX_ERROR_RECONNECT_ATTEMPTS) {
              debugLog(
                "clientInboundChannel error persists with unchanged token, giving up after max retries:",
                errorMessage
              );
              this.client.reconnectDelay = 0;
              this.client.deactivate();
              this._discardPendingConnectCallbacks(
                "clientInboundChannel retry cap reached"
              );
              if (onError) { onError(frame); }
              return;
            }

            debugLog(
              "clientInboundChannel error with unchanged token, retrying with backoff:",
              errorMessage
            );
            // Let stompjs auto-reconnect after the server closes the socket,
            // using exponential backoff (attempt counter is NOT reset here)
            this.client.reconnectDelay = this._getReconnectDelay();
            return;
          }

          if (
            errorMessage.includes("subscription") ||
            errorMessage.includes("authentication") ||
            errorMessage.includes("authorization") ||
            errorMessage.includes("group") ||
            errorMessage.includes("topic")
          ) {
            debugLog(
              "Subscription/Auth/Group/Topic error, will retry on next attempt:",
              errorMessage
            );
            return;
          }
        }

        if (onError) { onError(frame); }
      },
      onWebSocketClose: () => {
        this.connected = false;
        this._connecting = false;
        debugLog("WebSocket connection closed");
      },
      onWebSocketError: (error) => {
        debugLog("WebSocket Error:", error);
      },
    });

    this.client.activate();
  }

  isConnected() {
    return this.connected && this.client;
  }

  getConnectionStatus() {
    const subCount = this._subscriptionManager
      ? Object.keys(this._subscriptionManager.subscriptions).length
      : 0;
    return {
      connected: this.connected,
      hasClient: !!this.client,
      subscriptions: subCount,
    };
  }

  disconnect() {
    if (this.client) {
      if (this._subscriptionManager) {
        this._subscriptionManager._unsubscribeAll();
      }
      this.client.deactivate();
      this.connected = false;
      // Nothing is in flight after an explicit disconnect — leaving this true
      // would make the next connect() queue against an attempt that is gone
      this._connecting = false;
      this._connectedToken = null;
      // Message handlers are deliberately kept. Closing the socket and
      // forgetting who wanted to be told about messages are two different
      // things, and conflating them is how a routine reconnect silently
      // deafens the whole app: a guest upgrading from an anonymous connection
      // disconnects on the way, which used to wipe the app-level listener, so
      // the answer arrived with nobody left to receive it. A handler belongs to
      // whoever registered it and is removed through the function onMessage
      // returned them.
      this._discardPendingConnectCallbacks("disconnected");
      debugLog("Disconnected from WebSocket");
    }
  }
}

export default ConnectionManager;
